// ─── Encuesta de satisfacción — pública (sin login) ──────────
// Accedida via link único enviado al cliente 24h después de cerrar ticket.
// Ruta: /encuesta/ticket/:token

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Button } from '@components/ui/Button';
import { fetchEncuesta, responderEncuesta } from '@api/postventa';
import { Textarea } from '@components/ui/Input';
import { useTema, PASO } from '@components/M02/casaTema';

// ─── Star rating ──────────────────────────────────────────────
const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const T = useTema();
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform active:scale-90"
          style={{ transform: (hover || value) >= star ? 'scale(1.15)' : 'scale(1)' }}
          aria-label={`${star} estrella${star > 1 ? 's' : ''}`}
        >
          <Star
            size={40}
            style={{
              fill: (hover || value) >= star ? T.estados.validacion : 'transparent',
              color: (hover || value) >= star ? T.estados.validacion : T.fg3,
              transition: 'fill 0.15s, color 0.15s',
            }}
          />
        </button>
      ))}
    </div>
  );
};

const STAR_LABELS: Record<number, string> = {
  1: 'Muy insatisfecho',
  2: 'Insatisfecho',
  3: 'Neutral',
  4: 'Satisfecho',
  5: 'Muy satisfecho',
};

// ─── Página pública ───────────────────────────────────────────
const Encuesta: React.FC = () => {
  const T = useTema();
  const { tenant, token } = useParams<{ tenant: string; token: string }>();
  const [calificacion, setCalificacion] = useState(0);
  const [comentario, setComentario] = useState('');
  const [enviado, setEnviado] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['encuesta', token],
    queryFn: () => fetchEncuesta(tenant!, token!),
    enabled: !!(tenant && token),
    retry: false,
  });

  const responder = useMutation({
    mutationFn: () => responderEncuesta(tenant!, token!, { calificacion, comentario: comentario.trim() || undefined }),
    onSuccess: () => setEnviado(true),
  });

  // ── Shared card wrapper ───────────────────────────────────
  const Card = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: `linear-gradient(135deg,${T.cfoSoft} 0%,${T.accentSoft} 100%)` }}>
      <div className="w-full max-w-md rounded-2xl shadow-lg p-8"
        style={{ backgroundColor: T.panelSolid, border: `1px solid ${T.line}` }}>
        {children}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent" />
          <p style={{ color: T.fg2 }}>Cargando encuesta…</p>
        </div>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span style={{ fontSize: PASO.s24 }}>⚠️</span>
          <h2 className="text-lg font-semibold" style={{ color: T.fg }}>Enlace no válido</h2>
          <p className="text-sm" style={{ color: T.fg2 }}>
            Este enlace no es válido, ya expiró o hubo un error. Por favor contacta a tu asesor.
          </p>
        </div>
      </Card>
    );
  }

  if (data.token_expirado) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span style={{ fontSize: PASO.s24 }}>⏰</span>
          <h2 className="text-lg font-semibold" style={{ color: T.fg }}>Enlace expirado</h2>
          <p className="text-sm" style={{ color: T.fg2 }}>
            Este enlace de encuesta expiró (válido por 7 días). Si deseas compartir tu opinión, contacta a tu asesor.
          </p>
        </div>
      </Card>
    );
  }

  if (data.respondido) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span style={{ fontSize: PASO.s24 }}>✅</span>
          <h2 className="text-lg font-semibold" style={{ color: T.fg }}>Ya respondiste esta encuesta</h2>
          <p className="text-sm" style={{ color: T.fg2 }}>
            Tu opinión ya fue registrada. ¡Gracias por tu tiempo!
          </p>
        </div>
      </Card>
    );
  }

  if (enviado) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span style={{ fontSize: PASO.s24 }}>🎉</span>
          <h2 className="text-xl font-bold" style={{ color: T.fg }}>¡Muchas gracias!</h2>
          <p className="text-sm" style={{ color: T.fg2 }}>
            Tu calificación fue registrada y nos ayuda a mejorar el servicio.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Logo/brand */}
      <div className="flex items-center justify-center mb-6">
        <div className="text-2xl font-bold" style={{ color: T.estados.cfo }}>RM Desarrollos</div>
      </div>

      {/* Ticket info */}
      <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: T.panelSoft, border: `1px solid ${T.line}` }}>
        <p className="text-xs font-mono mb-1" style={{ color: T.fg3 }}>{data.folio}</p>
        <p className="text-sm font-medium" style={{ color: T.fg }}>{data.categoria}</p>
        <p className="text-xs mt-1 leading-relaxed line-clamp-3" style={{ color: T.fg2 }}>
          {data.descripcion}
        </p>
      </div>

      {/* Question */}
      <h2 className="text-lg font-semibold text-center mb-2" style={{ color: T.fg }}>
        ¿Cómo calificarías la atención a tu reporte?
      </h2>
      <p className="text-sm text-center mb-6" style={{ color: T.fg2 }}>
        Tu opinión nos ayuda a mejorar el servicio post-venta.
      </p>

      {/* Stars */}
      <div className="flex flex-col items-center gap-2 mb-6">
        <StarRating value={calificacion} onChange={setCalificacion} />
        {calificacion > 0 && (
          <span className="text-sm font-medium" style={{ color: T.estados.validacion }}>
            {STAR_LABELS[calificacion]}
          </span>
        )}
      </div>

      {/* Comment */}
      <div className="mb-6">
        <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: T.fg2 }}>
          Comentario (opcional)
        </label>
        <Textarea
          rows={3}
          value={comentario}
          onChange={e => setComentario(e.target.value)}
          placeholder="¿Qué podríamos mejorar? ¿Algo que destacar?"
          className="resize-none"
        />
      </div>

      {/* Submit */}
      <Button
        variant="primary"
        size="lg"
        fullWidth
        onClick={() => responder.mutate(undefined, { onError: () => {} })}
        disabled={calificacion === 0}
        loading={responder.isPending}
      >
        {responder.isPending ? 'Enviando…' : 'Enviar calificación'}
      </Button>

      {responder.isError && (
        <p className="text-xs text-center mt-2" style={{ color: T.danger }}>
          Ocurrió un error al enviar. Intenta de nuevo.
        </p>
      )}
    </Card>
  );
};

export default Encuesta;
